import { Box, Typography, Divider, CircularProgress } from "@mui/joy";
import { Plus } from "lucide-react";
import { useState } from "react";

import { DataGridSetting } from "../../../../../../components";
import { Button, Alert } from "../../../../../../shared";
import { useSupplier } from "../../../../../../hooks/query";

import { columnDef } from "./column-def";
import { InventoryForm } from "./InventoryForm";
import { Info } from "./GridFrameworks";

interface SupplierInventoryProps {
    supplierId: string;
}

export function SupplierInventory({ supplierId }: SupplierInventoryProps) {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingInventoryItem, setEditingInventoryItem] = useState<any | null>(null);
    const { useListInventory } = useSupplier();
    const { data, isLoading, isError, error } = useListInventory(supplierId);

    const handleCreate = () => {
        setEditingInventoryItem(null);
        setIsFormOpen(true);
    };

    const handleEdit = (inventoryItem: any) => {
        setEditingInventoryItem(inventoryItem);
        setIsFormOpen(true);
    };

    const handleClose = () => {
        setIsFormOpen(false);
        setEditingInventoryItem(null);
    };

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center" p={2}>
                <CircularProgress size="sm" />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box flex={1} p={2}>
                <Alert errorMessage={error || "Failed to load inventory"} />
            </Box>
        );
    }

    const inventory = (data as any[]) || [];

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%" }}>
            <Box ml={3} mt={2} mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography level="title-md" sx={{ fontWeight: 600 }}>
                        Supplier Inventory
                    </Typography>
                    <Typography level="body-sm" textColor="text.tertiary">
                        Manage inventory items for this supplier.
                    </Typography>
                </Box>
            </Box>
            <Divider />
            <Box flex={1} sx={{ overflow: "hidden" }}>
                <DataGridSetting
                    gridData={inventory}
                    columnDefs={columnDef.map((col) => {
                        if (col.cellRenderer === Info || col.field === "Id") {
                            return { ...col, cellRenderer: Info, cellRendererParams: { onEdit: handleEdit } };
                        }
                        return col;
                    })}
                    quickControlStartDecorator={<CreateSupplierInventoryButton onClick={handleCreate} />}
                />
            </Box>

            {isFormOpen && (
                <InventoryForm supplierId={supplierId} inventoryItem={editingInventoryItem} onClose={handleClose} />
            )}
        </Box>
    );
}

function CreateSupplierInventoryButton({ onClick }: { onClick: () => void }) {
    return (
        <Box display="flex" alignItems={"center"} ml={1.5}>
            <Button startDecorator={<Plus size={18} />} variant="soft" color="primary" onClick={onClick}>
                Add Inventory Item
            </Button>
            <Divider orientation="vertical" sx={{ mt: 1, mb: 1, ml: 2 }} />
        </Box>
    );
}
